<template>
  <div>
    <div v-if="order.expires" :class="[sizeClass, 'secondary--text mt-2']">
      <div class="d-flex align-center flex-wrap" style="gap: 6px">
        <span>
          <span>{{ isExpired ? "Expired at" : "Expires at" }} </span>
          <code :class="sizeClass">
            {{ order.expires | getExpiresDate }} {{ order.expires | getExpiresTime }} (GMT+{{ getGmtDiff }})
          </code>
        </span>

        <v-chip
          v-if="expiresCountdown && !isExpired"
          small
          :color="isExpiringSoon ? 'error' : 'warning'"
          text-color="white">
          <v-icon small left>mdi-clock-outline</v-icon>
          {{ expiresCountdown }}
        </v-chip>

        <div v-if="!isExpired && hasToraBooking">
          <v-btn small color="accent" dark depressed @click="openExtendDialog" :loading="extendLoading">
            <v-icon left :small="size === 'small'">mdi-clock-plus-outline</v-icon>
            Extend
          </v-btn>
        </div>

        <v-chip v-if="isExpired && !order.isFullyPaid" :small="size === 'small'" color="error" text-color="white">
          <v-icon :small="size === 'small'" left>mdi-clock-alert-outline</v-icon>
          EXPIRED
        </v-chip>
      </div>
    </div>

    <div v-else-if="!order.isFullyPaid" :class="[sizeClass, 'secondary--text mt-2']">
      <div class="d-flex align-center" style="gap: 6px">
        <span class="grey--text">No expiration date</span>
      </div>
    </div>

    <!-- Extend Expiration Dialog -->
    <v-dialog v-model="extendDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="primary white--text"> Extend Expiration Time </v-card-title>

        <v-card-text v-if="extendData" class="pt-5 px-6">
          <v-card v-if="isExpirationUnchanged" class="mb-4 pa-4" outlined style="background-color: #fff6d9">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-3">mdi-alert-circle-outline</v-icon>
              <span> The expiration time could not be extended and remains unchanged. </span>
            </div>
          </v-card>

          <table>
            <tbody>
              <tr>
                <td class="font-weight-medium py-2 label-cell">Old Expiration</td>
                <td class="font-weight-medium py-2 colon-cell pr-3">:</td>
                <td class="py-2 value-cell">
                  {{ extendData.oldExpiration | getExpiresDate }}
                  {{ extendData.oldExpiration | getExpiresTime }} (GMT+{{ getGmtDiff }})
                </td>
              </tr>

              <tr>
                <td class="font-weight-medium py-2 label-cell">New Expiration</td>
                <td class="font-weight-medium py-2 colon-cell pr-3">:</td>
                <td class="py-2 value-cell success--text font-weight-bold">
                  {{ extendData.newExpiration | getExpiresDate }}
                  {{ extendData.newExpiration | getExpiresTime }} (GMT+{{ getGmtDiff }})
                </td>
              </tr>

              <tr v-if="extendData.feePrice">
                <td class="font-weight-medium py-2 label-cell">Extension Fee</td>
                <td class="font-weight-medium py-2 colon-cell pr-3">:</td>
                <td class="py-2 value-cell font-weight-bold">
                  {{ extendData.feePrice.plainText }}
                </td>
              </tr>
            </tbody>
          </table>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <template v-if="isExpirationUnchanged">
            <v-btn color="primary" text @click="closeExtendDialog"> CLOSE </v-btn>
          </template>
          <template v-else>
            <v-btn color="grey" text @click="closeExtendDialog"> CANCEL </v-btn>
            <v-btn :loading="confirmLoading" color="primary" text @click="confirmExtension"> CONFIRM EXTENSION</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getErrorMessage } from "@/util/helpers";
import moment from "moment";
import "moment-timezone";
import Trainplanet from "@/util/trainplanet.api";

export default {
  name: "OrderExpirationExtender",
  props: {
    order: {
      type: Object,
      required: true,
    },
    size: {
      type: String,
      default: "small",
      validator: (value) => ["small", "medium", "large"].includes(value),
    },
  },
  data() {
    return {
      expiresCountdown: null,
      countdownInterval: null,
      extendLoading: false,
      extendDialog: false,
      extendData: null,
      confirmLoading: false,
    };
  },
  watch: {
    "order.expires": {
      handler() {
        this.orderStatusChecked = false;

        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }
        this.startExpiresCountdown();
      },
    },
  },
  computed: {
    sizeClass() {
      const sizeMap = {
        small: "caption",
        medium: "body-2",
        large: "body-1",
      };
      return sizeMap[this.size] || "caption";
    },
    getGmtDiff() {
      return moment().utcOffset() / 60;
    },
    isExpirationUnchanged() {
      if (!this.extendData) return false;

      const oldExpiration = moment(this.extendData.oldExpiration);
      const newExpiration = moment(this.extendData.newExpiration);

      return oldExpiration.isSame(newExpiration, "minute");
    },
    isExpired() {
      if (!this.order || !this.order.expires) return false;

      // Check if countdown has reached 00:00 or 00:00:00
      if (this.expiresCountdown === "00:00" || this.expiresCountdown === "00:00:00") {
        return true;
      }

      const expiresTime = moment.tz(this.order.expires, "Europe/Stockholm").local();
      const now = moment();

      return now.isAfter(expiresTime) || now.isSame(expiresTime);
    },
    isExpiringSoon() {
      if (!this.expiresCountdown || this.expiresCountdown === "00:00:00" || this.expiresCountdown === "00:00")
        return false;

      const expiresTime = moment.tz(this.order.expires, "Europe/Stockholm").local();
      const now = moment();
      const diff = expiresTime.diff(now);
      const duration = moment.duration(diff);

      return duration.asMinutes() < 5;
    },
    hasToraBooking() {
      return this.order.bookings && this.order.bookings.some((booking) => booking.externalSystem === "tora");
    },
  },
  filters: {
    getExpiresDate(datetime) {
      return moment.tz(datetime, "Europe/Stockholm").local().format("YYYY-MM-DD");
    },
    getExpiresTime(datetime) {
      return moment.tz(datetime, "Europe/Stockholm").local().format("HH:mm");
    },
  },
  mounted() {
    this.startExpiresCountdown();
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },
  methods: {
    startExpiresCountdown() {
      if (!this.order || !this.order.expires) return;

      const updateCountdown = () => {
        const expiresTime = moment.tz(this.order.expires, "Europe/Stockholm").local();
        const now = moment();
        const diff = expiresTime.diff(now);

        if (diff <= 0) {
          this.expiresCountdown = "00:00:00";
          if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
          }

          if (!this.orderStatusChecked) {
            this.checkOrderStatus();
          }

          return;
        }

        const duration = moment.duration(diff);
        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();
        const seconds = duration.seconds();

        if (days > 0) {
          this.expiresCountdown = `${days}d ${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        } else if (duration.asHours() >= 1) {
          const totalHours = Math.floor(duration.asHours());
          this.expiresCountdown = `${totalHours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        } else {
          this.expiresCountdown = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
        }
      };

      updateCountdown();
      this.countdownInterval = setInterval(updateCountdown, 1000);
    },
    async checkOrderStatus() {
      this.orderStatusChecked = true;

      try {
        const updatedOrder = await Trainplanet.getOrder(this.order.id);

        this.$emit("order-updated", updatedOrder, false);
      } catch (error) {
        console.error("Failed to check order status:", error);
      }
    },

    async openExtendDialog() {
      if (this.extendLoading) return;

      try {
        this.extendLoading = true;

        this.extendData = await Trainplanet.createOnHoldOffer(this.order.id);
        this.extendDialog = true;
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        this.extendLoading = false;
      }
    },

    closeExtendDialog() {
      this.extendDialog = false;
      this.extendData = null;
    },

    async confirmExtension() {
      if (this.confirmLoading || !this.extendData) return;

      try {
        this.confirmLoading = true;

        const updatedOrder = await Trainplanet.confirmOnHoldOffer(this.order.id);

        await this.$store.dispatch("success", "Expiration time has been extended successfully");

        this.$emit("order-updated", updatedOrder, true);

        this.closeExtendDialog();
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        this.confirmLoading = false;
      }
    },
  },
};
</script>
